
import React, { useState } from 'react';
import AppLayout from '@/components/app/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Filter } from 'lucide-react';

const AppShopConcerns = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  const concerns = [
    {
      name: "Fine Lines & Wrinkles",
      description: "Smooth and prevent signs of aging with targeted treatments",
      icon: "⚡",
      treatments: [
        { name: "Botox", credits: 8, href: "/app/treatments/botox" },
        { name: "Skin Tightening", credits: 10, href: "/app/treatments/skin-tightening" },
        { name: "RF Microneedling", credits: 12, href: "/app/treatments/rf-microneedling" }
      ],
      category: "Anti-Aging",
      href: "/conditions/fine-lines-wrinkles"
    },
    {
      name: "Acne & Scars",
      description: "Clear skin and reduce scarring with advanced therapies",
      icon: "🛡️",
      treatments: [
        { name: "AquaFirme Facial", credits: 4, href: "/app/treatments/aquafirme" },
        { name: "VI Peel", credits: 6, href: "/app/treatments/vi-peel" },
        { name: "RF Microneedling", credits: 12, href: "/app/treatments/rf-microneedling" }
      ],
      category: "Skin Health",
      href: "/conditions/acne-scars"
    },
    {
      name: "Pigmentation & Spots",
      description: "Even skin tone and reduce dark spots for radiant skin",
      icon: "☀️",
      treatments: [
        { name: "Laser Facial", credits: 8, href: "/app/treatments/laser-facial" },
        { name: "VI Peel", credits: 6, href: "/app/treatments/vi-peel" },
        { name: "Skin Boosters", credits: 8, href: "/app/treatments/skin-boosters" }
      ],
      category: "Skin Health",
      href: "/conditions/pigmentation-spots"
    },
    {
      name: "Dull & Dehydrated Skin",
      description: "Restore hydration and luminosity to tired skin",
      icon: "💧",
      treatments: [
        { name: "AquaFirme Facial", credits: 4, href: "/app/treatments/aquafirme" },
        { name: "Skin Boosters", credits: 8, href: "/app/treatments/skin-boosters" },
        { name: "IV Therapy", credits: 3, href: "/app/treatments/iv-therapy" }
      ],
      category: "Wellness",
      href: "/conditions/dull-dehydrated-skin"
    },
    {
      name: "Uneven Tone & Texture",
      description: "Smooth and refine skin texture for healthy glow",
      icon: "✨",
      treatments: [
        { name: "RF Microneedling", credits: 12, href: "/app/treatments/rf-microneedling" },
        { name: "Dermaplaning", credits: 3, href: "/app/treatments/dermaplaning" },
        { name: "AquaFirme Facial", credits: 4, href: "/app/treatments/aquafirme" }
      ],
      category: "Skin Health",
      href: "/conditions/uneven-tone-texture"
    },
    {
      name: "Body Fat & Double Chin",
      description: "Non-surgical fat reduction for defined profile",
      icon: "🎯",
      treatments: [
        { name: "Kybella", credits: 15, href: "/app/treatments/kybella" },
        { name: "Lipotropic Injections", credits: 2, href: "/app/treatments/lipotropic" },
        { name: "IV Energy Boost", credits: 3, href: "/app/treatments/iv-therapy" }
      ],
      category: "Body Contouring",
      href: "/conditions/body-fat-double-chin"
    }
  ];

  const categories = ['All', 'Anti-Aging', 'Skin Health', 'Wellness', 'Body Contouring'];
  
  const filteredConcerns = activeFilter === 'All' 
    ? concerns 
    : concerns.filter(concern => concern.category === activeFilter);

  return (
    <AppLayout>
      <div className="px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon" className="text-juv-deep-chestnut">
              <Link to="/app/shop">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
            <h1 className="text-2xl font-bold text-juv-deep-chestnut">Browse by Concern</h1>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {categories.map((category) => (
            <Button
              key={category}
              variant={activeFilter === category ? "default" : "outline"}
              size="sm"
              className={`whitespace-nowrap ${
                activeFilter === category 
                  ? 'bg-juv-terracotta hover:bg-juv-deep-chestnut text-white' 
                  : 'border-juv-rose-clay/30 text-juv-deep-chestnut hover:bg-juv-soft-blush/30'
              }`}
              onClick={() => setActiveFilter(category)}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Concerns Grid */}
        <div className="space-y-6">
          {filteredConcerns.map((concern, index) => (
            <Card key={index} className="overflow-hidden bg-white border-juv-rose-clay/20">
              <CardContent className="p-0">
                <div className="p-6">
                  {/* Concern Header */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-juv-soft-blush/30 rounded-full flex items-center justify-center text-2xl">
                      {concern.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-juv-deep-chestnut mb-2">
                        {concern.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3">
                        {concern.description}
                      </p>
                      <span className="inline-block px-3 py-1 bg-juv-rose-clay/20 text-juv-deep-chestnut text-xs rounded-full">
                        {concern.category}
                      </span>
                    </div>
                  </div>

                  {/* Treatments */}
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-juv-terracotta mb-3 uppercase tracking-wide">
                      Recommended Treatments
                    </h4>
                    <div className="space-y-3">
                      {concern.treatments.map((treatment, treatmentIndex) => (
                        <div key={treatmentIndex} className="flex items-center justify-between p-3 bg-juv-soft-blush/20 rounded-lg">
                          <div>
                            <p className="font-medium text-juv-deep-chestnut">{treatment.name}</p>
                            <p className="text-juv-terracotta text-sm font-semibold">
                              {treatment.credits} Credits
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button asChild size="sm" variant="outline" className="border-juv-rose-clay/30 text-juv-deep-chestnut hover:bg-juv-soft-blush/30">
                              <Link to={treatment.href}>Details</Link>
                            </Button>
                            <Button asChild size="sm" className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
                              <Link to="/app/book">Book</Link>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Learn More Button */}
                  <Button asChild variant="outline" className="w-full border-juv-rose-clay/30 text-juv-deep-chestnut hover:bg-juv-soft-blush/30">
                    <Link to={concern.href}>Learn More About This Concern</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quiz CTA */}
        <Card className="mt-8 bg-gradient-to-r from-juv-soft-blush/50 to-juv-rose-clay/30 border-juv-rose-clay/20">
          <CardContent className="p-6 text-center">
            <Sparkles className="w-8 h-8 text-juv-terracotta mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-juv-deep-chestnut mb-2">
              Not Sure What You Need?
            </h3>
            <p className="text-gray-600 mb-4">
              Take our personalized quiz to get matched with the perfect treatments for your unique concerns.
            </p>
            <Button asChild className="bg-juv-terracotta hover:bg-juv-deep-chestnut">
              <Link to="/quiz">Take the Glow Quiz</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default AppShopConcerns;
